import React, { useState, useEffect, useRef, useMemo } from 'react';

import axios from 'axios';

import { useAuth } from '../context/AuthContext';

import { useBalance } from '../context/BalanceContext';

import { usePrices } from '../context/PriceContext';

import LoadingSkeleton from '../components/LoadingSkeleton';

import { formatPrice, formatChange } from '../utils/numberFormat';

import { db } from '../firebase';

import { doc, getDoc, collection, query, where, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';



const TV_SCRIPT_SOURCES = [

  'https://s3.tradingview.com/tv.js',

  'https://www.tradingview.com/static/bundles/tv.js'

];



let tradingViewLoaderPromise = null;



const loadTradingViewScript = () => {

  if (typeof window === 'undefined') {

    return Promise.reject(new Error('TradingView unavailable in this environment'));

  }



  if (window.TradingView) {

    return Promise.resolve(window.TradingView);

  }



  if (tradingViewLoaderPromise) {

    return tradingViewLoaderPromise;

  }



  tradingViewLoaderPromise = new Promise((resolve, reject) => {

    let attemptIndex = 0;



    const tryNextSource = () => {

      if (window.TradingView) {

        resolve(window.TradingView);

        return;

      }



      if (attemptIndex >= TV_SCRIPT_SOURCES.length) {

        reject(new Error('Unable to load TradingView chart library.'));

        return;

      }



      const script = document.createElement('script');

      script.src = TV_SCRIPT_SOURCES[attemptIndex];

      script.async = true;

      script.crossOrigin = 'anonymous';

      script.dataset.tradingview = 'true';



      let timeoutId;



      const cleanup = () => {

        script.onload = null;

        script.onerror = null;

        if (timeoutId) {

          clearTimeout(timeoutId);

        }

      };



      script.onload = () => {

        cleanup();

        if (window.TradingView) {

          resolve(window.TradingView);

        } else {

          attemptIndex += 1;

          tryNextSource();

        }

      };



      script.onerror = () => {

        cleanup();

        script.remove();

        attemptIndex += 1;

        tryNextSource();

      };



      timeoutId = window.setTimeout(() => {

        cleanup();

        if (script.parentNode) {

          script.parentNode.removeChild(script);

        }

        attemptIndex += 1;

        tryNextSource();

      }, 8000);



      document.head.appendChild(script);

    };



    tryNextSource();

  });



  return tradingViewLoaderPromise;

};



const resetTradingViewLoader = () => {

  tradingViewLoaderPromise = null;

  if (typeof window !== 'undefined') {

    try {

      delete window.TradingView;

    } catch (err) {

      window.TradingView = undefined;

    }



    const orphanScripts = Array.from(document.querySelectorAll('script[data-tradingview="true"]'));

    orphanScripts.forEach((script) => {

      if (script.parentNode) {

        script.parentNode.removeChild(script);

      }

    });

  }

};



const TradingViewChart = ({ symbol, visible, onError }) => {

  const containerRef = useRef(null);

  const widgetContainerIdRef = useRef(`tradingview-chart-${Math.random().toString(36).slice(2)}`);

  const widgetInstanceRef = useRef(null);

  const [loadError, setLoadError] = useState(null);



  useEffect(() => {

    if (!visible) {

      if (typeof onError === 'function') {

        onError(null);

      }

      return undefined;

    }



    const containerEl = containerRef.current;

    if (!containerEl) {

      if (typeof onError === 'function') {

        onError('Chart container unavailable.');

      }

      return undefined;

    }



    let isCancelled = false;

    let frameCheckTimeoutId;

    let frameObserver;

    setLoadError(null);

    if (typeof onError === 'function') {

      onError(null);

    }



    const widgetTimeoutId = window.setTimeout(() => {

      if (!widgetInstanceRef.current && !isCancelled) {

        const timeoutMessage = 'TradingView connection timed out.';

        setLoadError((previous) => {

          if (previous) return previous;

          if (typeof onError === 'function') {

            onError(timeoutMessage);

          }

          return timeoutMessage;

        });

      }

    }, 9000);



    const initialiseWidget = (TradingViewLib) => {

      if (!containerEl || isCancelled) return;



      containerEl.innerHTML = '';



      widgetInstanceRef.current = new TradingViewLib.widget({

        width: '100%',

        height: 500,

        symbol,

        interval: '60',

        timezone: 'Etc/UTC',

        theme: 'dark',

        style: '1',

        locale: 'en',

        toolbar_bg: '#1e293b',

        enable_publishing: false,

        hide_top_toolbar: false,

        hide_legend: false,

        save_image: false,

        container_id: widgetContainerIdRef.current,

      });



      if (typeof onError === 'function') {

        onError(null);

      }



      if (widgetTimeoutId) {

        clearTimeout(widgetTimeoutId);

      }



      const cancelFrameWatch = () => {

        if (frameCheckTimeoutId) {

          clearTimeout(frameCheckTimeoutId);

          frameCheckTimeoutId = null;

        }

        if (frameObserver) {

          frameObserver.disconnect();

          frameObserver = null;

        }

      };



      frameCheckTimeoutId = window.setTimeout(() => {

        if (isCancelled) return;

        const iframe = containerEl.querySelector('iframe');

        if (!iframe) {

          const message = 'TradingView chart failed to render.';

          setLoadError((previous) => {

            if (previous) return previous;

            if (typeof onError === 'function') {

              onError(message);

            }

            return message;

          });

        }

        cancelFrameWatch();

      }, 6000);



      frameObserver = new MutationObserver(() => {

        const iframe = containerEl.querySelector('iframe');

        if (iframe) {

          cancelFrameWatch();

        }

      });



      frameObserver.observe(containerEl, { childList: true, subtree: true });

    };



    loadTradingViewScript()

      .then((TradingViewLib) => {

        if (!isCancelled) {

          initialiseWidget(TradingViewLib);

        }

      })

      .catch((error) => {

        if (!isCancelled) {

          const message = error?.message || 'Unable to display chart right now.';

          setLoadError(message);

          if (typeof onError === 'function') {

            onError(message);

          }

        }

      });



    return () => {

      isCancelled = true;

      if (widgetTimeoutId) {

        clearTimeout(widgetTimeoutId);

      }

      if (frameCheckTimeoutId) {

        clearTimeout(frameCheckTimeoutId);

      }

      if (frameObserver) {

        frameObserver.disconnect();

      }

      if (containerEl) {

        containerEl.innerHTML = '';

      }

      widgetInstanceRef.current = null;

    };

  }, [symbol, visible, onError]);



  if (!visible || loadError) return null;



  return <div id={widgetContainerIdRef.current} ref={containerRef} className="h-full w-full" />;

};



const FallbackPriceChart = ({

  coinName,

  symbol,

  data,

  loading,

  error,

  onRetry

}) => {

  const gradientId = useMemo(() => `sparkline-gradient-${Math.random().toString(36).slice(2)}`, []);



  if (loading) {

    return (

      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-white/10 bg-gray-900/60 text-center text-sm text-gray-200">

        <p className="font-semibold">Loading fallback market view…</p>

        <p className="mt-1 text-xs text-gray-400">Retrieving recent price history.</p>

      </div>

    );

  }



  if (error) {

    return (

      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-white/10 bg-gray-900/60 p-4 text-center text-sm text-red-300">

        <p className="font-semibold">Fallback data unavailable</p>

        <p className="mt-1 text-xs text-red-200/80">{error}</p>

        <button

          type="button"

          onClick={onRetry}

          className="mt-3 rounded-md border border-white/10 bg-gray-800 px-3 py-1 text-[11px] uppercase tracking-wide text-gray-200 hover:bg-gray-700"

        >

          Retry TradingView

        </button>

      </div>

    );

  }



  if (!data || data.length < 2) {

    return (

      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-white/10 bg-gray-900/60 p-4 text-center text-sm text-gray-300">

        <p className="font-semibold">Not enough market data</p>

        <p className="mt-1 text-xs text-gray-400">Please try another asset or retry TradingView.</p>

        <button

          type="button"

          onClick={onRetry}

          className="mt-3 rounded-md border border-white/10 bg-gray-800 px-3 py-1 text-[11px] uppercase tracking-wide text-gray-200 hover:bg-gray-700"

        >

          Retry TradingView

        </button>

      </div>

    );

  }



  const prices = data.map((entry) => Number(entry?.[1] ?? 0)).filter((value) => Number.isFinite(value));

  if (prices.length < 2) {

    return (

      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-white/10 bg-gray-900/60 p-4 text-center text-sm text-gray-300">

        <p className="font-semibold">Price data unavailable</p>

        <button

          type="button"

          onClick={onRetry}

          className="mt-3 rounded-md border border-white/10 bg-gray-800 px-3 py-1 text-[11px] uppercase tracking-wide text-gray-200 hover:bg-gray-700"

        >

          Retry TradingView

        </button>

      </div>

    );

  }



  const minPrice = Math.min(...prices);

  const maxPrice = Math.max(...prices);

  const firstPrice = prices[0];

  const lastPrice = prices[prices.length - 1];

  const range = maxPrice - minPrice || Math.max(maxPrice * 0.01, 1e-6);



  const pathCommands = prices

    .map((price, index) => {

      const x = prices.length === 1 ? 0 : (index / (prices.length - 1)) * 100;

      const y = 100 - ((price - minPrice) / range) * 100;

      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;

    })

    .join(' ');



  const areaPath = `${pathCommands} L 100 100 L 0 100 Z`;

  const percentChange = firstPrice ? ((lastPrice - firstPrice) / firstPrice) * 100 : 0;



  return (

    <div className="flex h-full flex-col justify-between rounded-xl border border-white/10 bg-gray-900/60 p-4">

      <div className="flex flex-wrap items-start justify-between gap-3">

        <div>

          <p className="text-[11px] uppercase tracking-[0.35em] text-gray-400">Fallback market view</p>

          <p className="mt-1 text-lg font-semibold text-white">

            {coinName || 'Selected Asset'}

            {symbol ? <span className="ml-2 text-sm text-gray-400">({symbol.toUpperCase()})</span> : null}

          </p>

        </div>

        <div className="text-right">

          <p className="text-2xl font-bold text-white">{formatPrice(lastPrice)}</p>

          <p className={`text-sm ${percentChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>

            {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(2)}%

          </p>

        </div>

      </div>



      <div className="relative mt-4 h-full w-full">

        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">

          <defs>

            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">

              <stop offset="0%" stopColor="rgba(59,130,246,0.35)" />

              <stop offset="100%" stopColor="rgba(59,130,246,0.05)" />

            </linearGradient>

          </defs>

          <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />

          <path d={pathCommands} stroke="#3b82f6" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />

        </svg>

      </div>



      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400">

        <span>High: {formatPrice(maxPrice)}</span>

        <span>Low: {formatPrice(minPrice)}</span>

        <button

          type="button"

          onClick={onRetry}

          className="rounded-md border border-white/10 bg-gray-800 px-3 py-1 text-[11px] uppercase tracking-wide text-gray-200 hover:bg-gray-700"

        >

          Retry TradingView

        </button>

      </div>

    </div>

  );

};



const options = [

  { label: '30%', seconds: 45, multiplier: 1.3, minAmount: 100 },

  { label: '60%', seconds: 120, multiplier: 1.6, minAmount: 15000 },

  { label: '90%', seconds: 240, multiplier: 1.9, minAmount: 70000 },

  { label: '180%', seconds: 600, multiplier: 2.8, minAmount: 310000 },

];



const formatTime = (seconds) => {

  const m = Math.floor(seconds / 60).toString().padStart(2, '0');

  const s = (seconds % 60).toString().padStart(2, '0');

  return `${m}:${s}`;

};



const formatTradeNumber = (value) => {

  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';

  return Number(value).toLocaleString('en-US', {

    minimumFractionDigits: 2,

    maximumFractionDigits: 2

  });

};



const formatProfitValue = (value) => {

  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';

  const num = Number(value);

  const formatted = Math.abs(num).toLocaleString('en-US', {

    minimumFractionDigits: 2,

    maximumFractionDigits: 2

  });

  const prefix = num > 0 ? '+' : num < 0 ? '-' : '';

  return `${prefix}${formatted}`;

};



function TradingPage() {

  const { currentUser, loading: loadingAuth, userData } = useAuth();

  const { balanceData, applyBalanceDelta } = useBalance();

  const { allCoins, loading: pricesLoading } = usePrices();

  const [selectedCoin, setSelectedCoin] = useState(null);

  const [tradingViewSymbol, setTradingViewSymbol] = useState('BINANCE:BTCUSDT');

  const [amount, setAmount] = useState('');

  const [selectedOption, setSelectedOption] = useState(options[0]);

  const [countdown, setCountdown] = useState(0);

  const [baseCurrency, setBaseCurrency] = useState('USDT');

  const [availableBalance, setAvailableBalance] = useState(0);

  

  // Update available balance when base currency or balance data changes

  useEffect(() => {

    if (!balanceData?.accountBalances?.trading) {

      setAvailableBalance(0);

      return;

    }



    const tradingBalances = balanceData.accountBalances.trading;

    const upperKey = baseCurrency.toUpperCase();

    const lowerKey = baseCurrency.toLowerCase();

    const balanceValue =

      tradingBalances[upperKey] ??

      tradingBalances[lowerKey] ??

      tradingBalances[baseCurrency] ??

      0;



    setAvailableBalance(Number.parseFloat(balanceValue) || 0);

  }, [balanceData, baseCurrency]);

  const [showCountdownPopup, setShowCountdownPopup] = useState(false);

  const [showResult, setShowResult] = useState(false);

  const [tradeResult, setTradeResult] = useState({ won: false, profit: 0 });

  // Remove duplicate balance state since we're using availableBalance

  const [tradeHistory, setTradeHistory] = useState([]);

  const [errorMessage, setErrorMessage] = useState('');

  const [chartVisible, setChartVisible] = useState(false);

  const [chartError, setChartError] = useState(null);

  const [fallbackData, setFallbackData] = useState(null);

  const [fallbackLoading, setFallbackLoading] = useState(false);

  const [fallbackError, setFallbackError] = useState(null);

  const [chartRetryNonce, setChartRetryNonce] = useState(0);

  const [showCoinSelect, setShowCoinSelect] = useState(false);

  const [showConfirmTrade, setShowConfirmTrade] = useState(false);

  const [pendingTradeDirection, setPendingTradeDirection] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');



  const filteredCoins = useMemo(() => {

    const dataset = Array.isArray(allCoins) ? allCoins : [];

    const term = searchTerm.trim().toLowerCase();

    if (!term) return dataset;

    return dataset.filter(

      (coin) =>

        coin?.name?.toLowerCase().includes(term) ||

        coin?.symbol?.toLowerCase().includes(term)

    );

  }, [allCoins, searchTerm]);



  useEffect(() => {

    if (!filteredCoins.length) {

      return;

    }



    setSelectedCoin((previous) => {

      if (previous) {

        const match = filteredCoins.find(

          (coin) =>

            coin.id === previous.id ||

            coin.symbol?.toUpperCase() === previous.symbol?.toUpperCase()

        );

        if (match === previous) {

          return previous;

        }

        if (match) {

          return match;

        }

      }

      return filteredCoins[0];

    });

  }, [filteredCoins]);



  useEffect(() => {

    if (selectedCoin) {

      setTradingViewSymbol(getTradingViewSymbol(selectedCoin));

    }

  }, [selectedCoin]);



  const getTradingViewSymbol = (coin) => {

    if (!coin) return 'BINANCE:BTCUSDT';

    if (coin.symbol.toUpperCase() === 'USDT') return 'BINANCE:USDTUSD';

    return `BINANCE:${coin.symbol.toUpperCase()}USDT`;

  };



  // This effect is no longer needed as we're using availableBalance from the state

  // which is already being updated by the earlier effect



  useEffect(() => {

    const userId = currentUser?.uid || currentUser?.id;

    if (!userId) return;



    const tradesQuery = query(

      collection(db, 'tradeRequests'),

      where('userId', '==', userId)

      // Removed orderBy to avoid composite index requirement

    );



    const unsubscribe = onSnapshot(tradesQuery, (snapshot) => {

      const history = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

        .sort((a, b) => {

          const tA = a.createdAt?.seconds || a.timestamp?.seconds || 0;

          const tB = b.createdAt?.seconds || b.timestamp?.seconds || 0;

          return tB - tA;

        })

        .slice(0, 20); // keep latest 20 client-side

      setTradeHistory(history);

    });



    return () => unsubscribe();

  }, [currentUser]);



  const handleSelectCoin = (coin) => {

    setSelectedCoin(coin);

    setTradingViewSymbol(getTradingViewSymbol(coin));

    setShowCoinSelect(false);

    setChartError(null);

    setFallbackData(null);

    setFallbackError(null);

    setFallbackLoading(false);

    resetTradingViewLoader();

    setChartRetryNonce((prev) => prev + 1);

    setSearchTerm('');

  };



  const handleTradeInitiation = (direction) => {

    if (!amount || Number(amount) <= 0) {

      setErrorMessage('Please enter a trade amount.');

      return;

    }

    if (Number(amount) < selectedOption.minAmount) {

      setErrorMessage(`Minimum amount for this contract is $${selectedOption.minAmount.toLocaleString()}`);

      return;

    }

    setPendingTradeDirection(direction);

    setShowConfirmTrade(true);

  };



  const handleRetryTradingView = () => {

    resetTradingViewLoader();

    setChartError(null);

    setFallbackData(null);

    setFallbackError(null);

    setFallbackLoading(false);

    setChartRetryNonce((prev) => prev + 1);

    setChartVisible(true);

  };



  const coinId = selectedCoin?.id;



  useEffect(() => {

    if (!chartError) {

      setFallbackLoading(false);

      setFallbackError(null);

      return;

    }



    if (!coinId) {

      setFallbackData(null);

      setFallbackError('Selected asset does not have a CoinGecko reference.');

      setFallbackLoading(false);

      return;

    }



    let isCancelled = false;

    setFallbackLoading(true);

    setFallbackError(null);



    axios

      .get(`https://api.coingecko.com/api/v3/coins/${coinId}/market_chart`, {

        params: {

          vs_currency: 'usd',

          days: 1,

          interval: 'hourly'

        }

      })

      .then((response) => {

        if (isCancelled) return;

        const priceSeries = response.data?.prices || [];

        setFallbackData(priceSeries);

        setFallbackLoading(false);

      })

      .catch((error) => {

        if (isCancelled) return;

        setFallbackError(error?.message || 'Unable to load fallback chart data.');

        setFallbackData(null);

        setFallbackLoading(false);

      });



    return () => {

      isCancelled = true;

    };

  }, [chartError, coinId]);



  const confirmTrade = () => {

    if (!pendingTradeDirection || !currentUser) return;

    

    if (!amount || Number(amount) <= 0) {

      setErrorMessage('Please enter a trade amount.');

      setShowConfirmTrade(false);

      return;

    }

    if (Number(amount) < selectedOption.minAmount) {

      setErrorMessage(`Minimum amount for this contract is $${selectedOption.minAmount.toLocaleString()}`);

      setShowConfirmTrade(false);

      return;

    }

    

    if (Number(amount) > availableBalance) {

      setErrorMessage("Insufficient balance");

      setShowConfirmTrade(false);

      return;

    }

    

    setErrorMessage('');

    setShowConfirmTrade(false);

    setShowCountdownPopup(true);

    setCountdown(selectedOption.seconds);

    

    const countdownInterval = setInterval(() => {

      setCountdown(prev => {

        if (prev <= 1) {

          clearInterval(countdownInterval);

          processTradeResult(pendingTradeDirection);

          return 0;

        }

        return prev - 1;

      });

    }, 1000);

  };



  const processingRef = useRef(false);



  const processTradeResult = async (direction) => {

    if (processingRef.current) return; // guard against double execution

    processingRef.current = true;

    setShowCountdownPopup(false);

    setErrorMessage('');



    try {

      const userId = currentUser?.uid || currentUser?.id;

      if (!userId) throw new Error('User not authenticated');



      const tradeRequest = {

        userId,

        asset: selectedCoin?.symbol?.toUpperCase() || 'BTC',

        direction: direction.toUpperCase(),

        amount: parseFloat(amount),

        price: selectedCoin?.current_price || 0,

        duration: selectedOption.seconds,

        multiplier: selectedOption.multiplier,

        status: 'pending',

        baseCurrency: baseCurrency.toUpperCase(),

        createdAt: serverTimestamp()

      };



      // Always fetch canWin fresh from Firestore so admin toggles take effect immediately
      const profileSnap = await getDoc(doc(db, 'userProfiles', userId));
      // Default to true if field missing (old accounts) — only false when admin explicitly sets false
      const canWinFlag = profileSnap.exists() ? (profileSnap.data()?.canWin ?? true) : true;

      const won = canWinFlag === true;



      // Determine profit / loss based on multiplier

      const multiplier = selectedOption.multiplier;

      const profitLossMultiplier = won ? (multiplier - 1) : -1;

      const amountValue = Number(amount);

      const profitLossAmount = amountValue * profitLossMultiplier;



      const stakeSuccess = await applyBalanceDelta(

        baseCurrency.toLowerCase(),

        -amountValue,

        'trading',

        `Contract ${direction.toUpperCase()} stake on ${selectedCoin?.symbol || 'BTC'}`

      );



      if (!stakeSuccess) {

        throw new Error('Failed to update balance');

      }



      const settlementDelta = amountValue + profitLossAmount;

      if (settlementDelta !== 0) {

        const settlementSuccess = await applyBalanceDelta(

          baseCurrency.toLowerCase(),

          settlementDelta,

          'trading',

          won

            ? `Contract win on ${selectedCoin?.symbol || 'BTC'} (+${(profitLossMultiplier * 100).toFixed(0)}%)`

            : `Contract loss on ${selectedCoin?.symbol || 'BTC'}`

        );



        if (!settlementSuccess) {

          throw new Error('Settlement failed — your stake has been deducted but the payout could not be applied. Please contact support to restore your balance.');

        }

      }



      // Create trade document

      const tradeRef = doc(collection(db, 'tradeRequests'));

      await setDoc(tradeRef, {

        ...tradeRequest,

        status: 'completed',

        outcome: won ? 'WIN' : 'LOSE',

        profit: profitLossAmount,

        price_movement: `${profitLossMultiplier > 0 ? '+' : ''}${(profitLossMultiplier * 100).toFixed(0)}%`,

        completedAt: serverTimestamp()

      });



      setTradeResult({

        pending: false,

        direction: tradeRequest.direction,

        amount: tradeRequest.amount,

        won,

        profit: profitLossAmount,

        priceMovement: profitLossMultiplier * 100

      });

      setShowResult(true);

    } catch (error) {

      console.error('Trade request error:', error);

      setErrorMessage(`Trade failed: ${error.message}`);

      setShowResult(true);

    } finally {

      // Reset processing flag to allow subsequent trades

      processingRef.current = false;

    }

  };





  if (loadingAuth || !currentUser) {

    return (

      <div className="flex items-center justify-center min-h-screen">

       {loadingAuth ? (

          <>

            <LoadingSkeleton width="80%" height={48} />

            <span className="ml-4 text-lg text-gray-300">Loading...</span>

          </>

        ) : (

          <span className="text-lg text-gray-300">Initializing user data...</span>

        )}

      </div>

    );

  }



  return (

    <div className="min-h-screen bg-gradient-to-b from-navy via-navyLight to-slate-950 text-slate-50 p-4 md:p-6">

      <div className="max-w-7xl mx-auto">

        <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">

          <div>

            <h1 className="text-2xl font-bold">Trading</h1>

          </div>

        </header>



        <section className="mb-6">

          <div className="rounded-2xl border border-white/5 bg-gray-900/70 p-5 shadow-lg shadow-black/30 backdrop-blur">

            <div className="flex items-center justify-between gap-4">

              <div>

                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400/90">Trading balance</p>

                <p className="mt-2 text-sm font-medium text-gray-300/90">Available in {baseCurrency}</p>

                <div className="mt-3 text-3xl font-bold text-white">{availableBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {baseCurrency}</div>

              </div>

              <div className="hidden sm:flex items-center gap-2 rounded-full border border-white/10 bg-gray-800/60 px-3 py-1 text-xs text-gray-300">

                <span className="inline-block h-2 w-2 rounded-full bg-emerald-400"></span>

                Real-time sync

              </div>

            </div>

          </div>

        </section>



        {availableBalance <= 0 && !pricesLoading && (

          <div className="mb-6 flex items-start gap-4 rounded-2xl border border-amber-500/20 bg-amber-500/6 p-4">

            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-400">

              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>

                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />

              </svg>

            </div>

            <div>

              <p className="text-sm font-semibold text-amber-200">No Trading Balance Available</p>

              <p className="mt-0.5 text-xs text-slate-400 leading-relaxed">

                Your Trading account has no {baseCurrency} funds. To place a contract, go to <strong className="text-slate-300">Profile</strong> and transfer funds from your Funding account into your Trading account first.

              </p>

              <a

                href="/profile"

                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30 px-3 py-1.5 text-xs font-semibold text-amber-300 transition hover:bg-amber-500/30"

              >

                Go to Profile and Transfer

              </a>

            </div>

          </div>

        )}

        {errorMessage && (

          <div className="mb-4 p-3 bg-red-900/50 text-red-300 rounded-lg">

            {errorMessage}

          </div>

        )}



        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <div className="lg:col-span-2 space-y-6">

            {chartVisible ? (

              <div className="bg-gray-900 rounded-2xl p-4">

                <div className="flex justify-between items-center mb-4 gap-4">

                  <div>

                    <h2 className="text-xl font-bold">

                      {selectedCoin ? `${selectedCoin.name} (${selectedCoin.symbol?.toUpperCase()})` : '--'}

                    </h2>

                    <div className="text-2xl font-bold">

                      {selectedCoin ? formatPrice(selectedCoin.current_price) : '--'}

                      {selectedCoin && (

                        <span className={`text-sm ml-2 ${selectedCoin.price_change_percentage_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>

                          {formatChange(selectedCoin.price_change_percentage_24h).value}

                        </span>

                      )}

                    </div>

                  </div>

                  <div className="flex gap-2">

                    <button 

                      onClick={() => setShowCoinSelect(true)}

                      className="flex items-center gap-1 px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm bg-blue-600 hover:bg-blue-700 rounded-lg transition"

                    >

                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">

                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />

                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />

                    </svg>

                    Choose Coin

                  </button>

                  <button 

                    onClick={() => setChartVisible(false)}

                    className="flex items-center gap-1 px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm bg-gray-800 hover:bg-gray-700 rounded-lg transition"

                  >

                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">

                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />

                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />

                    </svg>

                    Hide Chart

                  </button>

                </div>

              </div>

                

                <div className="h-[500px]">

                  {chartError ? (

                    <FallbackPriceChart

                      coinName={selectedCoin?.name}

                      symbol={selectedCoin?.symbol}

                      data={fallbackData}

                      loading={fallbackLoading}

                      error={fallbackError}

                      onRetry={handleRetryTradingView}

                    />

                  ) : (

                    <TradingViewChart

                      key={`${tradingViewSymbol}-${chartRetryNonce}`}

                      symbol={tradingViewSymbol}

                      visible={chartVisible}

                      onError={setChartError}

                    />

                  )}

                </div>

                {chartError && (

                  <div className="mt-4 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-200">

                    Unable to connect to TradingView. Displaying fallback price data instead.

                    {chartError ? ` (${chartError})` : ''}

                  </div>

                )}

              </div>

            ) : (

              <div className="flex justify-between items-center bg-gray-900 rounded-2xl p-4">

                <div>

                  <h2 className="text-xl font-bold">

                    {selectedCoin ? `${selectedCoin.name} (${selectedCoin.symbol?.toUpperCase()})` : '--'}

                  </h2>

                  <p className="text-2xl font-bold mt-1">

                    {selectedCoin ? formatPrice(selectedCoin.current_price) : '--'}

                    {selectedCoin && (

                      <span className={`block md:inline ml-0 md:ml-2 text-xs md:text-sm ${selectedCoin.price_change_percentage_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>

                        {formatChange(selectedCoin.price_change_percentage_24h).value}

                      </span>

                    )}

                  </p>

                </div>

                <div className="flex gap-2">

                  <button 

                    onClick={() => setShowCoinSelect(true)}

                    className="flex items-center gap-1 px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm bg-blue-600 hover:bg-blue-700 rounded-lg transition"

                  >

                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">

                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />

                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />

                    </svg>

                    Choose Coin

                  </button>

                  <button 

                    onClick={() => {

                      setChartVisible(true);

                      if (chartError) {

                        handleRetryTradingView();

                      }

                    }}

                    className="flex items-center gap-1 px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm bg-gray-800 hover:bg-gray-700 rounded-lg transition"

                  >

                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">

                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />

                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />

                    </svg>

                    Show Chart

                  </button>

                </div>

              </div>

            )}



            <div className="bg-gray-900 rounded-2xl p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div>

                  <div className="flex justify-between items-center mb-3">

                      <h3 className="font-bold">Trade Amount ({baseCurrency})</h3>

                      <select

                        value={baseCurrency}

                        onChange={(e) => setBaseCurrency(e.target.value.toUpperCase())}

                        className="bg-gray-700 text-white px-2 py-1 rounded"

                      >

                        <option value="USDT">USDT</option>

                        <option value="USDC">USDC</option>

                      </select>

                    </div>

                  <div className="bg-gray-800 rounded-xl p-4">

                    <div className="flex justify-between items-center mb-2">

                      <span className="text-gray-400">Amount</span>

                      <span className="text-sm text-white">Balance ({baseCurrency}): {formatPrice(availableBalance, baseCurrency).replace('$', '')}</span>

                    </div>

                    <div className="relative">

                      <input

                        type="number"

                        id="tradeAmount"

                        name="tradeAmount"

                        value={amount}

                        onChange={(e) => setAmount(e.target.value)}

                        placeholder={`Min: ${selectedOption.minAmount.toLocaleString()}`}

                        className="w-full bg-gray-700 text-white py-3 px-4 rounded-lg text-right text-xl font-bold placeholder:text-gray-500 placeholder:text-base placeholder:font-normal"

                        min={selectedOption.minAmount}

                        max={availableBalance}

                        autoComplete="off"

                      />

                      <span className="absolute left-4 top-3.5 text-gray-400">$</span>

                    </div>

                    <div className="grid grid-cols-4 gap-2 mt-3">

                      {[25, 50, 75, 100].map(percent => (

                        <button

                          key={percent}

                          onClick={() => setAmount(Math.min(availableBalance, (availableBalance * percent) / 100))}

                          className="bg-gray-700 hover:bg-gray-600 py-2 rounded-lg transition"

                        >

                          {percent}%

                        </button>

                      ))}

                    </div>

                  </div>

                </div>



                <div>

                  <h3 className="font-bold mb-3">Contract Options</h3>

                  <div className="grid grid-cols-2 gap-3">

                    {options.map(option => (

                      <button

                        key={option.label}

                        onClick={() => setSelectedOption(option)}

                        className={`p-4 rounded-xl transition ${

                          selectedOption.label === option.label

                            ? 'bg-blue-600 border-blue-500'

                            : 'bg-gray-800 border-gray-700 hover:bg-gray-700'

                        } border`}

                      >

                        <div className="font-bold">{option.label}</div>

                        <div className="text-sm text-gray-300">

                          {formatTime(option.seconds)}

                        </div>

                        <div className="text-xs text-gray-400 mt-1">

                          Min: ${option.minAmount.toLocaleString()}

                        </div>

                      </button>

                    ))}

                  </div>

                  

                  <div className="mt-4 bg-gray-800 rounded-xl p-4">

                    <div className="flex justify-between mb-2">

                      <span className="text-gray-400">Potential Profit:</span>

                      <span className="font-bold text-green-400">

                        +{formatPrice(amount * (selectedOption.multiplier - 1)).replace('$', '')}

                      </span>

                    </div>

                    <div className="flex justify-between">

                      <span className="text-gray-400">Potential Return:</span>

                      <span className="font-bold">

                        {((selectedOption.multiplier - 1) * 100).toFixed(0)}%

                      </span>

                    </div>

                  </div>

                </div>

              </div>

              

              <div className="grid grid-cols-2 gap-4 mt-6">

                <button

                  onClick={() => handleTradeInitiation('up')}

                  className="py-3.5 md:py-4 bg-green-600 hover:bg-green-700 rounded-xl font-bold transition flex items-center justify-center gap-2"

                >

                  <span className="text-sm md:text-base">BUY / UP</span>

                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" viewBox="0 0 20 20" fill="currentColor">

                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />

                  </svg>

                </button>

                <button

                  onClick={() => handleTradeInitiation('down')}

                  className="py-3.5 md:py-4 bg-red-600 hover:bg-red-700 rounded-xl font-bold transition flex items-center justify-center gap-2"

                >

                  <span className="text-sm md:text-base">SELL / DOWN</span>

                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" viewBox="0 0 20 20" fill="currentColor">

                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />

                  </svg>

                </button>

              </div>

            </div>

          </div>



          <div className="space-y-6">

            <div className="bg-gray-900 rounded-2xl p-4">

              <div className="flex justify-between items-center mb-4">

                <h3 className="font-bold">Trade History</h3>

                <button 

                  onClick={() => setTradeHistory([])} 

                  className="text-xs text-gray-400 hover:text-white"

                >

                  Clear

                </button>

              </div>

              <div className="text-sm text-white">

                <div className="grid grid-cols-5 text-gray-400 mb-2 px-2 text-xs">

                  <span>Asset</span>

                  <span className="text-center">Direction</span>

                  <span className="text-center">Amount</span>

                  <span className="text-center">Movement</span>

                  <span className="text-right">P/L</span>

                </div>

                <div className="max-h-[500px] overflow-y-auto">

                  {tradeHistory.length > 0 ? (

                    tradeHistory.map(trade => (

                      <div 

                        key={trade.id} 

                        className="grid grid-cols-5 py-2 px-2 border-b border-gray-800 hover:bg-gray-800/50 transition-colors text-xs"

                      >

                        <span className="font-medium">{trade.asset.toUpperCase()}</span>

                        <span className={`text-center font-medium ${

                          trade.direction === 'UP' ? 'text-green-400' : 'text-red-400'

                        }`}>

                          {trade.direction}

                        </span>

                        <span className="text-center">${formatTradeNumber(trade.amount)}</span>

                        <span className={`text-center ${

                          (trade.price_movement || '').toString().startsWith('+') ? 'text-green-400' : 'text-red-400'

                        }`}>

                          {trade.price_movement}

                        </span>

                        <span className={`text-right font-medium ${

                          trade.outcome === 'WIN' ? 'text-green-400' : 'text-red-400'

                        }`}>

                          {trade.profit !== undefined ? formatProfitValue(trade.profit) : '--'}

                        </span>

                      </div>

                    ))

                  ) : (

                    <div className="text-center py-8 text-gray-500">

                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />

                      </svg>

                      <p className="mt-2">No trades yet</p>

                      <p className="text-xs mt-1">Place your first trade to see history</p>

                    </div>

                  )}

                </div>

              </div>

            </div>

          </div>

        </div>



        {showCoinSelect && (

          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">

            <div className="flex w-full max-w-2xl max-h-[90vh] flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gradient-to-br from-gray-900 to-gray-800 shadow-2xl">

              <div className="flex items-center justify-between border-b border-gray-700 px-4 py-3">

                <h3 className="text-xl font-bold text-white">Select Trading Asset</h3>

                <button

                  type="button"

                  onClick={() => {

                    setShowCoinSelect(false);

                    setSearchTerm('');

                  }}

                  className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-700 hover:text-white"

                  aria-label="Close asset selector"

                >

                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">

                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />

                  </svg>

                </button>

              </div>



              <div className="px-4 py-4">

                <div className="relative mb-4">

                  <input

                    type="text"

                    id="coinSearch"

                    name="coinSearch"

                    placeholder="Search coins..."

                    className="w-full rounded-lg border border-gray-700 bg-gray-800 py-2.5 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"

                    value={searchTerm}

                    onChange={(event) => setSearchTerm(event.target.value)}

                    autoComplete="off"

                  />

                  <svg

                    xmlns="http://www.w3.org/2000/svg"

                    className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"

                    viewBox="0 0 20 20"

                    fill="currentColor"

                  >

                    <path

                      fillRule="evenodd"

                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"

                      clipRule="evenodd"

                    />

                  </svg>

                </div>



                <div className="max-h-[60vh] overflow-y-auto">

                  <table className="min-w-full border border-gray-700 text-sm text-white">

                    <thead className="sticky top-0 bg-gray-800 text-xs uppercase tracking-wide text-gray-400">

                      <tr>

                        <th className="px-2 py-2 text-left">#</th>

                        <th className="px-2 py-2 text-left">Coin</th>

                        <th className="px-2 py-2 text-right">Price</th>

                        <th className="px-2 py-2 text-right">24h</th>

                      </tr>

                    </thead>

                    <tbody>

                      {filteredCoins.map((coin, index) => (

                        <tr

                          key={coin.id}

                          className={`border-t border-gray-700 transition hover:bg-gray-800 ${

                            selectedCoin?.id === coin.id ? 'bg-gray-800' : ''

                          }`}

                          onClick={() => handleSelectCoin(coin)}

                        >

                          <td className="px-2 py-2">{index + 1}</td>

                          <td className="px-2 py-2">

                            <div className="flex items-center gap-2">

                              <img src={coin.image} alt={coin.name} className="h-5 w-5" />

                              <div>

                                <p className="font-semibold uppercase text-white">{coin.symbol}</p>

                                <p className="text-xs text-gray-400">{coin.name}</p>

                              </div>

                            </div>

                          </td>

                          <td className="px-2 py-2 text-right">{formatPrice(coin.current_price)}</td>

                          <td

                            className={`px-2 py-2 text-right font-semibold ${

                              coin.price_change_percentage_24h >= 0 ? 'text-green-400' : 'text-red-400'

                            }`}

                          >

                            {formatChange(coin.price_change_percentage_24h).value}

                          </td>

                        </tr>

                      ))}

                      {filteredCoins.length === 0 && (

                        <tr>

                          <td colSpan={4} className="px-2 py-6 text-center text-gray-500">

                            {pricesLoading ? 'Loading assets…' : 'No assets match your search.'}

                          </td>

                        </tr>

                      )}

                    </tbody>

                  </table>

                </div>

              </div>



              <div className="flex justify-end border-t border-gray-700 bg-gray-900/70 px-4 py-3">

                <button

                  type="button"

                  onClick={() => {

                    setShowCoinSelect(false);

                    setSearchTerm('');

                  }}

                  className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-semibold text-gray-200 transition hover:bg-gray-700"

                >

                  Close

                </button>

              </div>

            </div>

          </div>

        )}



        {showConfirmTrade && (

          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 p-4">

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-md w-full text-center shadow-xl border border-gray-800">

              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-900/30 flex items-center justify-center">

                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-400" viewBox="0 0 20 20" fill="currentColor">

                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 000-1.414z" clipRule="evenodd" />

                </svg>

              </div>

              

              <h3 className="text-2xl font-bold mb-4">Confirm Trade</h3>

              

              <div className="bg-gray-800 rounded-xl p-4 mb-6">

                <div className="grid grid-cols-2 gap-4 text-left">

                  <div>

                    <p className="text-gray-400 text-sm">Asset</p>

                    <p className="font-medium">{selectedCoin?.symbol?.toUpperCase() || '--'}</p>

                  </div>

                  <div>

                    <p className="text-gray-400 text-sm">Direction</p>

                    <p className={`font-bold ${pendingTradeDirection === 'up' ? 'text-green-400' : 'text-red-400'}`}>

                      {pendingTradeDirection === 'up' ? 'BUY / UP' : 'SELL / DOWN'}

                    </p>

                  </div>

                  <div>

                    <p className="text-gray-400 text-sm">Amount</p>

                    <p className="font-medium">{formatPrice(amount).replace('$', '')}</p>

                  </div>

                  <div>

                    <p className="text-gray-400 text-sm">Contract</p>

                    <p className="font-medium">{selectedOption.label}</p>

                  </div>

                </div>

                

                <div className="mt-4 pt-4 border-t border-gray-700">

                  <p className="text-gray-400 text-sm">Potential Profit</p>

                  <p className="text-xl font-bold text-green-400">

                    +${(amount * (selectedOption.multiplier - 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}

                  </p>

                </div>

              </div>

              

              <div className="flex gap-4">

                <button

                  onClick={() => setShowConfirmTrade(false)}

                  className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-bold transition"

                >

                  Cancel

                </button>

                <button

                  onClick={confirmTrade}

                  className={`flex-1 py-3 rounded-xl font-bold transition ${

                    pendingTradeDirection === 'up' 

                      ? 'bg-green-600 hover:bg-green-700' 

                      : 'bg-red-600 hover:bg-red-700'

                  }`}

                >

                  Confirm

                </button>

              </div>

            </div>

          </div>

        )}



        {showCountdownPopup && (

          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 p-4">

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 max-w-md w-full text-center shadow-xl border border-gray-800">

              <div className="relative w-32 h-32 mx-auto mb-6">

                <div className="absolute inset-0 rounded-full border-4 border-blue-500 animate-ping opacity-20"></div>

                <div className="absolute inset-0 rounded-full border-4 border-blue-500"></div>

                <div className="absolute inset-0 flex items-center justify-center">

                  <span className="text-4xl font-bold">{formatTime(countdown)}</span>

                </div>

              </div>

              

              <h3 className="text-2xl font-bold mb-2">Processing Trade</h3>

              

              <p className="text-gray-400">

                Your {selectedCoin?.symbol?.toUpperCase() || '--'} trade is being processed

              </p>

              <p className="text-gray-500 text-sm mt-4">

                Result will be available in {formatTime(countdown)}

              </p>

            </div>

          </div>

        )}



        {showResult && (

          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 p-4">

            <div className={`${

              tradeResult.won ? 'bg-gradient-to-br from-green-900/30 to-green-900/10' : 'bg-gradient-to-br from-red-900/30 to-red-900/10'

            } rounded-2xl p-8 max-w-md w-full text-center backdrop-blur-lg border ${

              tradeResult.won ? 'border-green-700' : 'border-red-700'

            } shadow-xl`}>

              <div className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6 ${

                tradeResult.won ? 'bg-green-900/30' : 'bg-red-900/30'

              }`}>

                {tradeResult.won ? (

                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-green-400" viewBox="0 0 20 20" fill="currentColor">

                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 000-1.414z" clipRule="evenodd" />

                  </svg>

                ) : (

                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-400" viewBox="0 0 20 20" fill="currentColor">

                    <path fillRule="evenodd" d="M8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />

                  </svg>

                )}

              </div>

              

              <h3 className={`text-2xl font-bold mb-2 ${

                tradeResult.won ? 'text-green-400' : 'text-red-400'

              }`}>

                {tradeResult.won ? 'Trade Successful!' : 'Trade Failed'}

              </h3>

              

              <div className="text-lg mb-2">

                <span className="text-gray-300">Direction:</span> 

                <span className={`ml-2 font-bold ${

                  tradeResult.direction === 'UP' ? 'text-green-400' : 'text-red-400'

                }`}>

                  {tradeResult.direction}

                </span>

              </div>

              

              <div className="text-lg mb-4">

                <span className="text-gray-300">Price moved:</span> 

                <span className={`ml-2 font-bold ${

                  tradeResult.priceMovement > 0 ? 'text-green-400' : 'text-red-400'

                }`}>

                  {tradeResult.priceMovement > 0 ? '+' : ''}{tradeResult.priceMovement}%

                </span>

              </div>

              

              <div className="text-3xl font-bold my-4">

                {tradeResult.profit > 0 ? '+' : ''}{formatPrice(Math.abs(tradeResult.profit)).replace('$', '')}

              </div>

              

              <div className="mb-6 text-gray-300">

                {tradeResult.won 

                  ? `You earned ${formatPrice(amount * (selectedOption.multiplier - 1))}` 

                  : `You lost ${formatPrice(amount)}`}

              </div>

              

              <button

                onClick={() => setShowResult(false)}

                className={`${

                  tradeResult.won ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'

                } py-3 px-8 rounded-full font-bold transition w-full`}

              >

                Continue Trading

              </button>

            </div>

          </div>

        )}

      </div>

    </div>

  );

}



export default TradingPage;

